import { useState, useMemo } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Trash2, TrendingDown, TrendingUp, Minus } from "lucide-react";
import { cn } from "@/lib/utils";
import { LineChart, Line, XAxis, YAxis, Tooltip, ResponsiveContainer, ReferenceLine } from "recharts";

interface WeightEntry {
  id: string;
  weight: number;
  logged_at: string;
  notes?: string;
}

interface WeightHistoryModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  weightHistory: WeightEntry[];
  onDelete: (id: string) => void;
  goalWeight?: number;
}

export function WeightHistoryModal({ 
  open, 
  onOpenChange, 
  weightHistory,
  onDelete,
  goalWeight
}: WeightHistoryModalProps) {
  const [deletingId, setDeletingId] = useState<string | null>(null);

  const handleDelete = async (id: string) => {
    setDeletingId(id);
    await onDelete(id);
    setDeletingId(null);
  };

  // Sort by date descending (most recent first) for list
  const sortedHistory = [...weightHistory].sort(
    (a, b) => new Date(b.logged_at).getTime() - new Date(a.logged_at).getTime()
  );

  // Sort ascending for chart
  const chartData = useMemo(() => {
    return [...weightHistory]
      .sort((a, b) => new Date(a.logged_at).getTime() - new Date(b.logged_at).getTime())
      .map(entry => ({
        date: new Date(entry.logged_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric' }),
        weight: entry.weight,
        fullDate: entry.logged_at
      }));
  }, [weightHistory]);

  const getWeightChange = (index: number): { diff: number; direction: 'up' | 'down' | 'same' } => {
    if (index >= sortedHistory.length - 1) return { diff: 0, direction: 'same' };
    const current = sortedHistory[index].weight;
    const previous = sortedHistory[index + 1].weight;
    const diff = current - previous;
    return {
      diff,
      direction: diff > 0 ? 'up' : diff < 0 ? 'down' : 'same'
    };
  };

  const allWeights = [...chartData.map(d => d.weight), ...(goalWeight ? [goalWeight] : [])];
  const minWeight = allWeights.length > 0 ? Math.min(...allWeights) - 5 : 0;
  const maxWeight = allWeights.length > 0 ? Math.max(...allWeights) + 5 : 100;
  const avgWeight = chartData.length > 0 ? chartData.reduce((sum, d) => sum + d.weight, 0) / chartData.length : 0;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="glass-card border-border/50 max-w-md max-h-[85vh] overflow-hidden flex flex-col">
        <DialogHeader>
          <DialogTitle className="font-display text-xl">Weight History</DialogTitle>
        </DialogHeader>

        {/* Weight Graph */}
        {chartData.length >= 2 && (
          <div className="h-40 w-full mb-4">
            <ResponsiveContainer width="100%" height="100%">
              <LineChart data={chartData} margin={{ top: 5, right: 5, left: -20, bottom: 5 }}>
                <XAxis 
                  dataKey="date" 
                  tick={{ fontSize: 10, fill: 'hsl(var(--muted-foreground))' }}
                  axisLine={{ stroke: 'hsl(var(--border))' }}
                  tickLine={false}
                />
                <YAxis 
                  domain={[minWeight, maxWeight]}
                  tick={{ fontSize: 10, fill: 'hsl(var(--muted-foreground))' }}
                  axisLine={{ stroke: 'hsl(var(--border))' }}
                  tickLine={false}
                />
                <Tooltip 
                  contentStyle={{ 
                    backgroundColor: 'hsl(var(--card))', 
                    border: '1px solid hsl(var(--border))',
                    borderRadius: '8px',
                    fontSize: '12px'
                  }}
                  labelStyle={{ color: 'hsl(var(--foreground))' }}
                />
                <ReferenceLine 
                  y={avgWeight} 
                  stroke="hsl(var(--muted-foreground))" 
                  strokeDasharray="3 3" 
                  strokeOpacity={0.5}
                />
                {goalWeight && (
                  <ReferenceLine 
                    y={goalWeight} 
                    stroke="hsl(var(--primary))" 
                    strokeDasharray="5 5" 
                    strokeWidth={2}
                    label={{ 
                      value: `Goal: ${goalWeight}`, 
                      position: 'right',
                      fill: 'hsl(var(--primary))',
                      fontSize: 10
                    }}
                  />
                )}
                <Line 
                  type="monotone" 
                  dataKey="weight" 
                  stroke="hsl(var(--primary))" 
                  strokeWidth={2}
                  dot={{ fill: 'hsl(var(--primary))', strokeWidth: 0, r: 3 }}
                  activeDot={{ r: 5, fill: 'hsl(var(--primary))' }}
                />
              </LineChart>
            </ResponsiveContainer>
          </div>
        )}

        <div className="flex-1 overflow-y-auto space-y-2 pr-2">
          {sortedHistory.length === 0 ? (
            <div className="text-center py-8">
              <p className="text-muted-foreground">No weight entries yet</p>
              <p className="text-xs text-muted-foreground mt-1">
                Tap your weight in Progress to log your first entry
              </p>
            </div>
          ) : (
            sortedHistory.map((entry, index) => {
              const { diff, direction } = getWeightChange(index);
              return (
                <div 
                  key={entry.id}
                  className="flex items-center justify-between p-3 rounded-xl bg-secondary/30 hover:bg-secondary/50 transition-colors"
                >
                  <div className="flex items-center gap-3">
                    <div className={cn(
                      "w-8 h-8 rounded-lg flex items-center justify-center",
                      direction === 'down' && "bg-green-500/20",
                      direction === 'up' && "bg-red-500/20",
                      direction === 'same' && "bg-muted"
                    )}>
                      {direction === 'down' && <TrendingDown className="w-4 h-4 text-green-400" />}
                      {direction === 'up' && <TrendingUp className="w-4 h-4 text-red-400" />}
                      {direction === 'same' && <Minus className="w-4 h-4 text-muted-foreground" />}
                    </div>
                    <div>
                      <p className="font-display font-bold text-lg">{entry.weight} lbs</p>
                      <p className="text-xs text-muted-foreground">
                        {new Date(entry.logged_at).toLocaleDateString('en-US', {
                          weekday: 'short',
                          month: 'short',
                          day: 'numeric',
                          year: 'numeric'
                        })}
                      </p>
                    </div>
                  </div>
                  <div className="flex items-center gap-3">
                    {index < sortedHistory.length - 1 && diff !== 0 && (
                      <span className={cn(
                        "text-sm font-medium",
                        direction === 'down' && "text-green-400",
                        direction === 'up' && "text-red-400"
                      )}>
                        {diff > 0 ? '+' : ''}{diff.toFixed(1)}
                      </span>
                    )}
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => handleDelete(entry.id)}
                      disabled={deletingId === entry.id}
                      className="h-8 w-8 p-0 text-muted-foreground hover:text-red-400"
                    >
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  </div>
                </div>
              );
            })
          )}
        </div>

        {sortedHistory.length > 0 && (
          <div className="pt-4 border-t border-border/30">
            <div className="flex justify-between text-sm">
              <span className="text-muted-foreground">Total entries</span>
              <span className="font-medium">{sortedHistory.length}</span>
            </div>
            {sortedHistory.length > 1 && (
              <div className="flex justify-between text-sm mt-1">
                <span className="text-muted-foreground">Total change</span>
                {(() => {
                  const totalChange = sortedHistory[0].weight - sortedHistory[sortedHistory.length - 1].weight;
                  return (
                    <span className={cn(
                      "font-medium",
                      totalChange < 0 && "text-green-400",
                      totalChange > 0 && "text-red-400"
                    )}>
                      {totalChange > 0 ? '+' : ''}{totalChange.toFixed(1)} lbs
                    </span>
                  );
                })()}
              </div>
            )}
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}